import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Serializable filter value types (for Redux storage)
export type SerializedTextFilter = {
  type: "text";
  value: string;
};

export type SerializedNumberFilter = {
  type: "number";
  operator: "gt" | "lt" | "eq" | "between";
  number1: number | null;
  number2: number | null;
};

export type SerializedDateFilter = {
  type: "date";
  operator: "isAfter" | "isBefore" | "isExact" | "isBetween";
  date1: string | null; // ISO string
  date2: string | null; // ISO string
};

export type SerializedBooleanFilter = {
  type: "boolean";
  value: boolean | null;
};

export type SerializedTaxonomyFilter = {
  type: "taxonomy";
  taxonomyKey: string;
  value: number[];
};

export type SerializedFilter =
  | SerializedTextFilter
  | SerializedNumberFilter
  | SerializedDateFilter
  | SerializedBooleanFilter
  | SerializedTaxonomyFilter;

export type ColumnFilterState = {
  [columnKey: string]: SerializedFilter;
};

export type TableFilterState = {
  [tableId: string]: ColumnFilterState;
};

export type TableFiltersState = {
  tables: TableFilterState;
};

const initialState: TableFiltersState = {
  tables: {},
};

export const tableFiltersSlice = createSlice({
  name: "tableFilters",
  initialState,
  reducers: {
    setColumnFilter: (
      state,
      action: PayloadAction<{
        tableId: string;
        columnKey: string;
        filter: SerializedFilter;
      }>,
    ) => {
      const { tableId, columnKey, filter } = action.payload;
      if (!state.tables[tableId]) {
        state.tables[tableId] = {};
      }
      state.tables[tableId][columnKey] = filter;
    },
    clearColumnFilter: (
      state,
      action: PayloadAction<{
        tableId: string;
        columnKey: string;
      }>,
    ) => {
      const { tableId, columnKey } = action.payload;
      if (state.tables[tableId]) {
        delete state.tables[tableId][columnKey];
      }
    },
    clearTableFilters: (
      state,
      action: PayloadAction<{
        tableId: string;
      }>,
    ) => {
      const { tableId } = action.payload;
      state.tables[tableId] = {};
    },
    clearAllFilters: (state) => {
      state.tables = {};
    },
  },
});

export const tableFiltersReducer = tableFiltersSlice.reducer;

export const {
  setColumnFilter,
  clearColumnFilter,
  clearTableFilters,
  clearAllFilters,
} = tableFiltersSlice.actions;

// Selectors
export const selectTableFilters = (
  state: { tableFilters: TableFiltersState },
  tableId: string,
) => state.tableFilters.tables[tableId] || {};

export const selectColumnFilter = (
  state: { tableFilters: TableFiltersState },
  tableId: string,
  columnKey: string,
) => state.tableFilters.tables[tableId]?.[columnKey];

export const selectHasActiveFilters = (
  state: { tableFilters: TableFiltersState },
  tableId: string,
) => {
  const tableFilters = state.tableFilters.tables[tableId];
  return tableFilters && Object.keys(tableFilters).length > 0;
};

// Helper functions for filter application
export const isTextFilterActive = (filter: SerializedTextFilter): boolean => {
  return filter.value.trim() !== "";
};

export const isNumberFilterActive = (
  filter: SerializedNumberFilter,
): boolean => {
  return filter.number1 !== null;
};

export const isDateFilterActive = (filter: SerializedDateFilter): boolean => {
  return filter.date1 !== null;
};

export const isBooleanFilterActive = (
  filter: SerializedBooleanFilter,
): boolean => {
  return filter.value !== null;
};

export const isTaxonomyFilterActive = (
  filter: SerializedTaxonomyFilter,
): boolean => {
  return filter.value.length > 0;
};

export const isFilterActive = (filter: SerializedFilter): boolean => {
  switch (filter.type) {
    case "text":
      return isTextFilterActive(filter);
    case "number":
      return isNumberFilterActive(filter);
    case "date":
      return isDateFilterActive(filter);
    case "boolean":
      return isBooleanFilterActive(filter);
    case "taxonomy":
      return isTaxonomyFilterActive(filter);
    default:
      return false;
  }
};

// Filter application functions
export const applyTextFilter = (
  value: any,
  filter: SerializedTextFilter,
): boolean => {
  if (!isTextFilterActive(filter)) return true;
  if (value === null || value === undefined) return false;
  return String(value)
    .toLowerCase()
    .includes(filter.value.toLowerCase().trim());
};

export const applyNumberFilter = (
  value: any,
  filter: SerializedNumberFilter,
): boolean => {
  if (!isNumberFilterActive(filter)) return true;
  if (value === null || value === undefined) return false;
  const numValue = Number(value);
  if (isNaN(numValue)) return false;

  switch (filter.operator) {
    case "eq":
      return numValue === filter.number1;
    case "gt":
      return numValue > (filter.number1 ?? -Infinity);
    case "lt":
      return numValue < (filter.number1 ?? Infinity);
    case "between":
      return (
        numValue >= (filter.number1 ?? -Infinity) &&
        numValue <= (filter.number2 ?? Infinity)
      );
    default:
      return true;
  }
};

export const applyDateFilter = (
  value: any,
  filter: SerializedDateFilter,
): boolean => {
  if (!isDateFilterActive(filter)) return true;
  if (value === null || value === undefined) return false;

  const dateValue = new Date(value);
  if (isNaN(dateValue.getTime())) return false;

  const filterDate1 = filter.date1 ? new Date(filter.date1) : null;
  const filterDate2 = filter.date2 ? new Date(filter.date2) : null;

  switch (filter.operator) {
    case "isExact":
      if (!filterDate1) return true;
      return dateValue.toDateString() === filterDate1.toDateString();
    case "isAfter":
      if (!filterDate1) return true;
      return dateValue > filterDate1;
    case "isBefore":
      if (!filterDate1) return true;
      return dateValue < filterDate1;
    case "isBetween":
      if (!filterDate1 || !filterDate2) return true;
      return dateValue >= filterDate1 && dateValue <= filterDate2;
    default:
      return true;
  }
};

export const applyBooleanFilter = (
  value: any,
  filter: SerializedBooleanFilter,
): boolean => {
  if (!isBooleanFilterActive(filter)) return true;
  return value === filter.value;
};

export const applyTaxonomyFilter = (
  value: any,
  filter: SerializedTaxonomyFilter,
): boolean => {
  if (!isTaxonomyFilterActive(filter)) return true;
  if (value === null || value === undefined) return false;
  return filter.value.includes(Number(value));
};

export const applyFilter = (value: any, filter: SerializedFilter): boolean => {
  switch (filter.type) {
    case "text":
      return applyTextFilter(value, filter);
    case "number":
      return applyNumberFilter(value, filter);
    case "date":
      return applyDateFilter(value, filter);
    case "boolean":
      return applyBooleanFilter(value, filter);
    case "taxonomy":
      return applyTaxonomyFilter(value, filter);
    default:
      return true;
  }
};

// Apply all column filters to a dataset
export const applyColumnFilters = <T extends Record<string, any>>(
  data: T[],
  filters: ColumnFilterState,
  getFieldValue?: (row: T, columnKey: string) => any,
): T[] => {
  const activeFilters = Object.entries(filters).filter(([, filter]) =>
    isFilterActive(filter),
  );

  if (activeFilters.length === 0) return data;

  return data.filter((row) =>
    activeFilters.every(([columnKey, filter]) => {
      const value = getFieldValue
        ? getFieldValue(row, columnKey)
        : row[columnKey];
      return applyFilter(value, filter);
    }),
  );
};
